// demo-controls — default skin module
// Token disposition: pass-through (no overrides)
// Panel chrome: hardcoded dark glass palette (MVR D5 / MVR Part 16 architectural invariant —
//   token-driven chrome would transform with page theme/skin, breaking meta-UI isolation)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: contents;
  }

  [part="panel"] {
    position: fixed;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding-block: 6px;
    padding-inline: 8px;
    background: rgba(30, 30, 30, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 9999px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: var(--run-demo-controls-z-index, 9999);
  }

  [part="panel"][data-position="bottom-right"] {
    inset-block-end: var(--run-demo-controls-offset, 16px);
    inset-inline-end: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][data-position="bottom-left"] {
    inset-block-end: var(--run-demo-controls-offset, 16px);
    inset-inline-start: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][data-position="top-right"] {
    inset-block-start: var(--run-demo-controls-offset, 16px);
    inset-inline-end: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][data-position="top-left"] {
    inset-block-start: var(--run-demo-controls-offset, 16px);
    inset-inline-start: var(--run-demo-controls-offset, 16px);
  }

  [part="announcer"] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;

/**
 * Render the demo-controls shadow DOM.
 *
 * Stability contract (important for test correctness):
 *   - The panel element (id="run-demo-controls-toolbar") is created once and
 *     reused on every render. Its attributes are updated in-place.
 *   - The announcer element (aria-live="polite") is created once and never
 *     replaced. This lets the headless's #announce() method set textContent
 *     synchronously before the panel children are regenerated, so test
 *     references to the announcer element remain valid across renders.
 *   - Only the panel's *children* (buttons) are replaced via innerHTML on
 *     each render, avoiding stale-reference failures on the panel or announcer.
 *
 * @param {RunDemoControls} host
 */
export function render(host) {
  const { collapsed, locale, locales, pageSkin, position } = host;

  const strings = (host._strings && host._strings[locale]) || {};

  // Compute next locale in cycle
  const localesArr = Array.isArray(locales) ? locales : [];
  const currentIdx = localesArr.findIndex(function (l) { return l.code === locale; });
  const nextLocaleIdx = (currentIdx === -1 ? 0 : (currentIdx + 1)) % (localesArr.length || 1);
  const nextLocale = localesArr.length > 0 ? localesArr[nextLocaleIdx] : null;

  // State-dependent aria-labels
  const toolbarLabel  = strings.toolbar_label    || 'Design system demo controls';
  const themeLabel    = host.theme === 'dark'
    ? (strings.theme_to_light    || 'Switch to light theme')
    : (strings.theme_to_dark     || 'Switch to dark theme');
  const skinLabel     = pageSkin === 'default'
    ? (strings.skin_to_wireframe || 'Switch to wireframe skin')
    : (strings.skin_to_default   || 'Switch to default skin');
  const toggleLabel   = collapsed
    ? (strings.expand            || 'Expand controls')
    : (strings.collapse          || 'Collapse controls');
  const toggleExpanded = collapsed ? 'false' : 'true';
  const toggleTabindex = collapsed ? '0' : '-1';

  const shadow = host.shadowRoot;

  // ------------------------------------------------------------------
  // Ensure stable panel element (upsert by id, never replace)
  // ------------------------------------------------------------------
  let panel = shadow.getElementById('run-demo-controls-toolbar');
  if (!panel) {
    panel = document.createElement('div');
    panel.setAttribute('part', 'panel');
    panel.setAttribute('role', 'toolbar');
    panel.setAttribute('aria-orientation', 'horizontal');
    panel.setAttribute('id', 'run-demo-controls-toolbar');
    shadow.appendChild(panel);
  }

  // Update panel attributes on every render
  panel.setAttribute('aria-label', toolbarLabel);
  panel.setAttribute('data-position', position);

  // ------------------------------------------------------------------
  // Ensure stable announcer element (upsert, never replace or move)
  // ------------------------------------------------------------------
  let announcer = shadow.querySelector('[aria-live="polite"]');
  if (!announcer) {
    announcer = document.createElement('span');
    announcer.setAttribute('part', 'announcer');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    shadow.appendChild(announcer);
  }

  // ------------------------------------------------------------------
  // Regenerate panel children (buttons) on every render
  //
  // Toggle button is always inside the panel.
  // Action buttons (theme, locale, skin) are only rendered when expanded.
  // data-action="collapse" is used for the toggle in both states so
  // #handleClick can find it by a single action name.
  // ------------------------------------------------------------------

  const localeButtonMarkup = (!collapsed && localesArr.length > 0 && nextLocale)
    ? `<run-button variant="ghost" size="sm" data-action="locale" aria-label="${
        (strings.locale_to_next || 'Switch locale to {label}').replace('{label}', nextLocale.label)
      }" tabindex="-1"></run-button>`
    : '';

  const actionButtonsMarkup = collapsed ? '' : `<run-button variant="ghost" size="sm" data-action="theme" aria-label="${themeLabel}" tabindex="0"></run-button>${localeButtonMarkup}<run-button variant="ghost" size="sm" data-action="skin" aria-label="${skinLabel}" tabindex="-1"></run-button>`;

  panel.innerHTML = `${actionButtonsMarkup}<run-button variant="ghost" size="sm" data-action="collapse" aria-label="${toggleLabel}" aria-expanded="${toggleExpanded}" aria-controls="run-demo-controls-toolbar" tabindex="${toggleTabindex}"></run-button>`;
}
